import axios from 'axios';
import { setStatsData } from '../slices/stats/statsSlice';
import {
  studyHistoryLoading,
  studyHistoryReceived,
  studyHistoryFailed,
} from '../slices/studyHistory/studyHistorySlice';
import axiosInstance from './axiosCreator';
import { store } from '../store/store';
import {
  todayStudyHistoryLoading,
  todayStudyHistoryReceived,
  todayStudyHistoryFailed,
} from '../slices/todayStudyHistory/todayStudyHistorySlice';
import type { TopicStat } from '../types/Dashboard';
import { parseContentDispositionFilename } from '../utils/downloadFile';
import { nowInDateTimezone } from '../utils/timezoneDate';
import type { TopicTreeView } from './topics';

export const fetchAllExamStats = async () => {
  try {
    store.dispatch(studyHistoryLoading('all'));
    const response = await axiosInstance.get('/stats/all-exams');

    store.dispatch(
      studyHistoryReceived({
        scope: 'all',
        data: response.data.data,
      })
    );

    return response.data.data;
  } catch (error) {
    console.error(error);

    store.dispatch(
      studyHistoryFailed({
        scope: 'all',
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch all exam stats',
      })
    );
  }
};

export const fetchPrimaryExamStats = async () => {
  try {
    store.dispatch(studyHistoryLoading('primaryExam'));
    const response = await axiosInstance.get('/stats/primary-exam');

    store.dispatch(
      studyHistoryReceived({
        scope: 'primaryExam',
        data: response.data.data,
      })
    );

    return response.data.data;
  } catch (error) {
    console.error(error);

    store.dispatch(
      studyHistoryFailed({
        scope: 'primaryExam',
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch primary exam stats',
      })
    );
  }
};

/**
 * Day the free-plan daily counters apply to, in the user's timezone — the same
 * timezone the backend counts `submissionDate` in.
 */
export const getTodayStudyHistoryDateKey = (): string =>
  nowInDateTimezone(store.getState().user.profile?.timezone).format(
    'YYYY-MM-DD'
  );

export const fetchPrimaryExamTodayStats = async () => {
  // Stamped before the request so a call spanning midnight can't label the
  // previous day's totals as the new day's.
  const dateKey = getTodayStudyHistoryDateKey();

  try {
    store.dispatch(todayStudyHistoryLoading());
    const response = await axiosInstance.get('/stats/primary-exam/today');

    if (response.data.success) {
      store.dispatch(
        todayStudyHistoryReceived({ data: response.data.data, dateKey })
      );
    } else {
      store.dispatch(todayStudyHistoryFailed(response.data.error));
    }

    return response.data.data;
  } catch (error) {
    console.error(error);

    store.dispatch(
      todayStudyHistoryFailed(
        error instanceof Error
          ? error.message
          : "Failed to fetch today's primary exam stats"
      )
    );
  }
};

let inFlightDateKey: string | null = null;

/**
 * Fetch today's counters unless they are already loaded for today or a fetch
 * for today is in flight. Surfaces that gate on the free-plan daily limits call
 * this on mount: the slice is ephemeral and holds a single day, so without it
 * they would gate on either a previous day's totals or no data at all
 * (ORA-319).
 */
export const refreshTodayStudyHistoryIfStale = async () => {
  const dateKey = getTodayStudyHistoryDateKey();

  if (store.getState().todayStudyHistory.dateKey === dateKey) return;
  if (inFlightDateKey === dateKey) return;

  inFlightDateKey = dateKey;

  try {
    await fetchPrimaryExamTodayStats();
  } finally {
    inFlightDateKey = null;
  }
};

export const fetchGeneralUserStats = async () => {
  try {
    const response = await axiosInstance.get('/stats/general');
    if (!response.data.success) {
      throw new Error(response.data.error);
    }
    store.dispatch(setStatsData(response.data.data));
    return response.data.data;
  } catch (error) {
    console.error(error);
  }
};

export const fetchPreviousQBankScore = async (examId: string) => {
  try {
    const response = await axiosInstance.get(
      `/stats/previousQbankScore/${examId}`
    );

    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const fetchVanityStats = async (examId?: string) => {
  const params = examId ? `?examId=${examId}` : '';
  const response = await axiosInstance.get(`/api/v5/stats/vanity${params}`);
  return response.data.data;
};

export const fetchHistoryStats = async (examId?: string, days = 63) => {
  const params = new URLSearchParams();
  if (examId) params.append('examId', examId);
  params.append('days', days.toString());
  const response = await axiosInstance.get(`/api/v5/stats/history?${params}`);
  return response.data.data;
};

export const fetchQbankPerformanceStats = async (
  examId?: string,
  timeframe = 30
) => {
  const params = new URLSearchParams();
  if (examId) params.append('examId', examId);
  params.append('timeframe', timeframe.toString());

  const response = await axiosInstance.get(
    `/api/v5/stats/qbank-performance?${params}`
  );

  return response.data.data;
};

export const fetchFlashcardProgressStats = async (examId?: string) => {
  const params = examId ? `?examId=${examId}` : '';

  const response = await axiosInstance.get(
    `/api/v5/stats/flashcard-progress${params}`
  );

  return response.data.data;
};

export const fetchTopicPerformanceStats = async (
  examId?: string,
  view?: TopicTreeView
) => {
  const params = new URLSearchParams();
  if (examId) params.append('examId', examId);
  if (view) params.append('view', view);

  const response = await axiosInstance.get(
    `/api/v5/stats/topic-performance${params.toString() ? `?${params.toString()}` : ''}`
  );

  return response.data.data;
};

export const fetchTopicTreeStats = async (
  examId?: string,
  view?: TopicTreeView
) => {
  const params = new URLSearchParams();
  if (examId) params.append('examId', examId);
  if (view) params.append('view', view);
  try {
    const response = await axiosInstance.get(
      `/api/v5/stats/topic-tree${params.toString() ? `?${params.toString()}` : ''}`
    );

    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.code === 'ERR_NETWORK') {
      return null;
    }

    throw error;
  }
};

/** The `/api/v5/stats/topic-tree` envelope. */
export interface TopicTreeStatsResponse {
  success: boolean;
  data: TopicStat[];
  /** Present (true) only when the student may see readiness (ORA-1022). */
  readinessEnabled?: true;
  /** Present (true) only when Session Summary ranking may use readiness. */
  readinessRankingEnabled?: true;
}

export interface TopicTreeStatsWithReadiness {
  tree: TopicStat[];
  readinessEnabled: boolean;
  readinessRankingEnabled: boolean;
}

/**
 * `fetchTopicTreeStats` for the student's own `/performance` tree: also reads
 * the envelope's `readinessEnabled`, which gates every readiness surface.
 * Returns null on a network error, like `fetchTopicTreeStats`.
 */
export const fetchTopicTreeStatsWithReadiness = async (
  examId?: string,
  view?: TopicTreeView
): Promise<TopicTreeStatsWithReadiness | null> => {
  const params = new URLSearchParams();
  if (examId) params.append('examId', examId);
  if (view) params.append('view', view);
  try {
    const response = await axiosInstance.get<TopicTreeStatsResponse>(
      `/api/v5/stats/topic-tree${params.toString() ? `?${params.toString()}` : ''}`
    );

    return {
      tree: response.data.data,
      readinessEnabled: response.data.readinessEnabled === true,
      readinessRankingEnabled: response.data.readinessRankingEnabled === true,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.code === 'ERR_NETWORK') {
      return null;
    }

    throw error;
  }
};

export const fetchProgressStats = async (examId: string) => {
  const response = await axiosInstance.get(
    `/api/v5/stats/progress?examId=${examId}`
  );

  return response.data.data;
};

export const downloadPerformanceExport = async (
  examId?: string
): Promise<{ blob: Blob; filename: string | null }> => {
  const response = await axiosInstance.get<Blob>('/api/v5/stats/export', {
    params: examId === undefined ? undefined : { examId },
    responseType: 'blob',
  });

  return {
    blob: response.data,
    filename: parseContentDispositionFilename(
      response.headers['content-disposition'] as string | undefined
    ),
  };
};
